import { generateClient } from 'aws-amplify/api';
import { logApiEvent, logger } from 'src/analytics/KatalLogger';
import { getTimeDifference } from 'src/utils/date-time-utilities';
import moment from 'moment-timezone';

// Enhanced error information interface
interface EnhancedErrorInfo {
  message: string;
  statusCode?: number;
  errorCode?: string;
  requestId?: string;
  path?: string[];
  errorType?: string;
}

/** Ticketing template users file when an unexpected error persists. */
export const SUPPORT_TICKET_URL = 'https://t.corp.amazon.com/create/templates/cbda2d4a-511c-48c2-8e23-81e1c6242e76';

/**
 * Shown for unknown/unmapped errors
 */
export const GENERIC_SYSTEM_ERROR = `Something went wrong. Please try again. If the issue persists, cut a ticket at ${SUPPORT_TICKET_URL}`;

/**
 * 1:1 port of genericMutationResponse.vtl's error mapping — same checks against the RAW message, in
 * the same order — so the known cases behave exactly as before (no regressions). P0001 / 28000 keep
 * their extracted human message; the specific Postgres errors map to fixed copy. The ONE deliberate
 * change: when NOTHING matches, return GENERIC_SYSTEM_ERROR instead of leaking the raw message.
 *
 * The RDS data source wraps PostgreSQL errors as:  RDSHttp:{"message":"ERROR: <msg>; SQLState: XXXXX"}
 */
export const sanitizeDbError = (raw: string): string => {
  const message = (raw ?? '').toString();

  // Extract the human-readable text: substring after "ERROR:", trimmed at "; SQLState".
  let extracted = '';
  const errStart = message.indexOf('ERROR:');
  if (errStart >= 0) extracted = message.slice(errStart + 6).split('; SQLState')[0].trim();

  if (message.includes('28000') || message.includes('UNAUTHORIZED')) return extracted || GENERIC_SYSTEM_ERROR;
  if (message.includes('P0001') || message.includes('SQLState: P0001')) return extracted || GENERIC_SYSTEM_ERROR;
  if (message.includes('permission denied')) return "You don't have permission to access this resource";
  if (message.includes('Duplicate record exists')) return 'Duplicate record exists';
  if (message.includes('column') && message.includes('does not exist')) return 'Invalid database column specified';
  if (message.includes('table') && message.includes('does not exist')) return 'The requested database table does not exist';
  if (message.includes('does not exist')) return 'The requested resource was not found';
  if (message.includes('duplicate key value')) return 'Resource already exists with the same unique key';
  if (message.includes('violates foreign key constraint')) return 'Operation violates database relationship constraints';
  if (message.includes('violates check constraint')) return 'Data validation failed';
  if (message.includes('syntax error')) return 'Invalid query syntax';
  if (message.includes('connection')) return 'Unable to connect to database';
  if (message.includes('timeout')) return 'Database operation timed out';

  // Nothing matched → generic message (the VTL kept the extracted text here; we deliberately don't).
  return GENERIC_SYSTEM_ERROR;
};

// Utility to format error messages for UI display
export const formatApiErrors = (errors: any[]): string => {
  if (errors.length === 0) return 'Unknown error occurred';

  const firstError = errors[0];
  // Keep the actual (unsanitised) error in the logs for debugging — only the returned value is user-facing.
  logger.error('[GraphQL API] raw error', { message: firstError.message, error: firstError });
  // Resolvers using genericMutationResponse.vtl already return a clean, curated message here — trust it as-is.
  // Every other resolver passed the raw DB error through, so sanitise that.
  if (firstError.errorInfo?.errorMessage) return firstError.errorInfo.errorMessage;
  return sanitizeDbError(firstError.message || 'Unknown error occurred');
};

// Utility to extract enhanced error information for logging
export const extractErrorInfo = (errors: any[]): EnhancedErrorInfo[] => {
  return errors.map((error) => ({
    message: error.message || 'Unknown error',
    statusCode: error.errorInfo?.statusCode,
    errorCode: error.errorInfo?.errorCode,
    requestId: error.errorInfo?.requestId,
    path: error.path,
    errorType: error.errorType
  }));
};

interface GraphQLRequestOptions {
  query: any;
  variables: any;
  requestName: string;
  requestType: 'query' | 'mutation';
}

export const fetchGraphQLRequest = async <T>({ query, variables, requestName, requestType }: GraphQLRequestOptions): Promise<T> => {
  const initiateTimer = moment();
  let response: any;
  let elapsed: number;

  try {
    const client = generateClient();
    response = await client.graphql({
      query: query,
      variables: variables
    });
    elapsed = getTimeDifference(initiateTimer);

    if (response.errors && response.errors.length > 0) {
      const errorMessage = formatApiErrors(response.errors);
      // Log all enhanced error information for debugging
      const enhancedErrorInfo = extractErrorInfo(response.errors);
      logApiEvent(
        {
          requestType: requestType,
          requestName: requestName,
          requestStatus: 'API_FAILED',
          responseTime: elapsed
        },
        { errors: response.errors, enhancedErrorInfo }
      );
      throw new Error(errorMessage);
    }

    logApiEvent({
      requestType: requestType,
      requestName: requestName,
      requestStatus: 'API_SUCCESS',
      responseTime: elapsed
    });

    return response.data;
  } catch (error: any) {
    elapsed = getTimeDifference(initiateTimer);
    const errorMessage = error.errors ? formatApiErrors(error.errors) : 'API Request Failed: Unexpected error occurred';
    logApiEvent(
      {
        requestType: requestType,
        requestName: requestName,
        requestStatus: 'API_FAILED',
        responseTime: elapsed
      },
      { errorMessage }
    );

    throw new Error(errorMessage);
  }
};
